import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Sparkles, MapPin, User as UserIcon, Info, Share2, Copy } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";
import RichTextEditor from "../../components/RichTextEditor";

export default function AdminSeriesEdit() {
  const nav = useNavigate();
  const { id } = useParams();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [campuses, setCampuses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/campuses").then((r) => setCampuses(r.data));
    // Slot-based programs a series can belong to (Capital Series, Founder Labs, …).
    api.get("/programs").then((r) => setPrograms((r.data || []).filter((p) => p.delivery === "slot_series"))).catch(() => {});
    api.get(`/sessions/series/${id}`)
      .then((r) => {
        const s = r.data;
        setForm({
          title: s.title || "",
          program_id: s.program_id || "",
          investor_name: s.investor_name || "",
          investor_firm: s.investor_firm || "",
          investor_bio: s.investor_bio || "",
          investor_photo: s.investor_photo || "",
          investor_linkedin: s.investor_linkedin || "",
          focus_areas_str: (s.focus_areas || []).join(", "),
          campus_id: s.campus_id || "cmp_pa",
          booking_mode: s.booking_mode || "approval",
          cancellation_cutoff_hours: s.cancellation_cutoff_hours || 24,
          booking_fields: s.booking_fields || {
            name: { enabled: true, required: true }, email: { enabled: true, required: true },
            linkedin: { enabled: true, required: false }, website: { enabled: true, required: false },
            note: { enabled: true, required: true },
          },
          active: s.active !== false,
          _readonly: { day_of_week: s.day_of_week, start_hour: s.start_hour, end_hour: s.end_hour, slot_duration_minutes: s.slot_duration_minutes },
        });
      })
      .catch(() => { toast.error("Series not found"); nav("/admin/events"); })
      .finally(() => setLoading(false));
  }, [id, nav]);

  const submit = async () => {
    if (!form.title || !form.investor_name || !form.investor_firm) return toast.error("Title, investor name and firm are required");
    setBusy(true);
    try {
      const { focus_areas_str, _readonly, ...rest } = form;
      const payload = { ...rest, focus_areas: focus_areas_str.split(",").map((s) => s.trim()).filter(Boolean) };
      // Empty = leave unassigned; the backend drops nulls, so this never clears an
      // existing assignment or trips the program_id FK with "".
      payload.program_id = rest.program_id || null;
      await api.patch(`/sessions/series/${id}`, payload);
      toast.success("Saved");
      nav("/admin/events");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (loading || !form) {
    return <AdminShell><div className="skeleton h-64" /></AdminShell>;
  }

  return (
    <AdminShell testId="admin-series-edit">
        <div className="max-w-3xl">
          <button onClick={() => nav("/admin/events")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-4">
            <ArrowLeft size={14} /> All events
          </button>

          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-2xl flex items-center justify-center" style={{ background: "hsl(var(--vd-purple) / 0.28)", color: "hsl(var(--vd-purple-ink))" }}>
              <Sparkles size={16} />
            </div>
            <div>
              <Overline>Founder Labs</Overline>
              <h1 className="text-3xl font-heading font-medium tracking-tight">Edit session</h1>
            </div>
          </div>

          {/* Read-only slot config notice */}
          <div className="rounded-2xl p-4 flex items-start gap-3 mb-6" style={{ background: "hsl(var(--cream))", color: "hsl(var(--vd-peach-ink))" }} data-testid="edit-slot-notice">
            <Info size={14} className="mt-0.5 shrink-0" />
            <div className="text-xs">
              <div className="font-medium">Slot schedule is fixed at creation</div>
              <div className="mt-0.5 font-mono" style={{ color: "hsl(var(--vd-peach-ink) / 0.85)" }}>
                {form._readonly.day_of_week} · {form._readonly.start_hour}:00–{form._readonly.end_hour}:00 PT · {form._readonly.slot_duration_minutes} min slots
              </div>
              <div className="mt-1 text-muted-foreground">To change the date, time window, or slot length, delete this series and create a new one.</div>
            </div>
          </div>

          {/* Title */}
          <div className="card-hair p-6 space-y-5">
            <Field label="Session title">
              <input className="input-field" placeholder="e.g. One-on-one session with Sree Menon" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title" />
              <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                <span>Shown on the public Founder Labs page and in all emails.</span>
                {form.title_source === "auto" && (
                  <span className="chip text-[10px]" style={{ background: "hsl(var(--vd-peach) / 0.18)", color: "hsl(var(--vd-peach-ink))" }} data-testid="edit-title-auto-chip">
                    Auto-generated · edit to customise
                  </span>
                )}
              </div>
            </Field>

            {/* Investor block */}
            <div>
              <div className="flex items-center gap-2 mb-3"><UserIcon size={13} style={{ color: "hsl(var(--vd-green))" }} /><Overline>Investor</Overline></div>
              <div className="grid md:grid-cols-[96px_1fr] gap-4">
                <div className="w-24">
                  <ImageUpload value={form.investor_photo} onChange={(url) => setForm({ ...form, investor_photo: url })} aspect="square" testId="edit-investor-photo-upload" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input className="input-field" placeholder="Investor name" value={form.investor_name} onChange={(e) => setForm({ ...form, investor_name: e.target.value })} data-testid="edit-investor-name" />
                  <input className="input-field" placeholder="Firm" value={form.investor_firm} onChange={(e) => setForm({ ...form, investor_firm: e.target.value })} data-testid="edit-investor-firm" />
                  <input className="input-field md:col-span-2" placeholder="LinkedIn (linkedin.com/in/…)" value={form.investor_linkedin} onChange={(e) => setForm({ ...form, investor_linkedin: e.target.value })} data-testid="edit-investor-linkedin" />
                  <div className="md:col-span-2">
                    <RichTextEditor value={form.investor_bio} onChange={(html) => setForm({ ...form, investor_bio: html })} placeholder="Short bio / description — use bold, links, and lists to give founders context…" minRows={4} testId="edit-investor-bio" />
                  </div>
                  <input className="input-field md:col-span-2" placeholder="Focus areas (comma-separated)" value={form.focus_areas_str} onChange={(e) => setForm({ ...form, focus_areas_str: e.target.value })} data-testid="edit-focus" />
                </div>
              </div>
            </div>

            <Field label="Program">
              <select className="input-field" value={form.program_id || ""} onChange={(e) => setForm({ ...form, program_id: e.target.value })} data-testid="edit-program">
                <option value="">— Unassigned —</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <div className="text-xs text-muted-foreground mt-2">
                Which program this series runs. Sets the badge and (later) the member booking allowance.
              </div>
            </Field>

            {/* Campus + booking mode */}
            <div className="grid md:grid-cols-2 gap-4">
              <Field icon={MapPin} label="Campus">
                <select className="input-field" value={form.campus_id} onChange={(e) => setForm({ ...form, campus_id: e.target.value })} data-testid="edit-campus">
                  {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Booking mode">
                <div className="flex gap-1 p-1 bg-white/40 rounded-2xl border border-white/60">
                  <ModePill active={form.booking_mode === "first_come"} onClick={() => setForm({ ...form, booking_mode: "first_come" })} testId="edit-mode-fcfs">First-come</ModePill>
                  <ModePill active={form.booking_mode === "approval"} onClick={() => setForm({ ...form, booking_mode: "approval" })} testId="edit-mode-approval">Approval</ModePill>
                </div>
              </Field>
            </div>

            <Field label="Cancellation cutoff (hours)">
              <input type="number" min={0} className="input-field" value={form.cancellation_cutoff_hours} onChange={(e) => setForm({ ...form, cancellation_cutoff_hours: parseInt(e.target.value) || 0 })} data-testid="edit-cutoff" />
            </Field>

            {/* Booking fields */}
            <div>
              <Overline>Founder submission fields</Overline>
              <div className="mt-3 space-y-2">
                {Object.entries(form.booking_fields).map(([k, cfg]) => (
                  <div key={k} className="flex items-center justify-between p-2.5 rounded-xl border border-white/60 bg-white/40" data-testid={`edit-field-${k}`}>
                    <div>
                      <div className="text-sm font-medium capitalize">{k === "note" ? "What are you working on?" : k}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{k}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                        <input type="checkbox" checked={cfg.enabled} onChange={(e) => setForm({ ...form, booking_fields: { ...form.booking_fields, [k]: { ...cfg, enabled: e.target.checked } } })} className="accent-primary" data-testid={`edit-field-${k}-enabled`} />
                        Show
                      </label>
                      <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                        <input type="checkbox" checked={cfg.required} disabled={!cfg.enabled} onChange={(e) => setForm({ ...form, booking_fields: { ...form.booking_fields, [k]: { ...cfg, required: e.target.checked } } })} className="accent-primary" data-testid={`edit-field-${k}-required`} />
                        Required
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-primary" data-testid="edit-active" />
                Active (shows in public events list)
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-between items-center gap-3 flex-wrap">
            <ShareCapitalLink id={id} />
            <button onClick={submit} disabled={busy} className="btn-primary" data-testid="edit-submit"><Save size={13} />{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </div>
    </AdminShell>
  );
}

function ShareCapitalLink({ id }) {
  const [link, setLink] = useState(null);
  const gen = async () => {
    let url;
    try {
      const r = await api.post(`/sessions/series/${id}/share-token`);
      url = `${window.location.origin}/e/${r.data.token}`;
      setLink(url);
    } catch { toast.error("Failed to generate link"); return; }
    try { await navigator.clipboard.writeText(url); toast.success("Locked link copied to clipboard"); }
    catch { toast.success("Link ready — use Copy to copy it"); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="capital-share-block">
      <button onClick={gen} type="button" className="btn-ghost text-sm" data-testid="capital-private-share-btn">
        <Share2 size={12} /> Generate locked share link
      </button>
      {link && (
        <div className="flex items-center gap-2 p-2 rounded-xl bg-white/60 border border-white/60" data-testid="capital-private-share-panel">
          <input readOnly value={link} className="input-field text-xs font-mono w-72" onFocus={(e) => e.target.select()} data-testid="capital-private-share-link" />
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} className="btn-ghost text-xs" data-testid="capital-private-share-copy" type="button"><Copy size={11} /></button>
        </div>
      )}
    </div>
  );
}

function ModePill({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} className={`flex-1 px-3 py-2 rounded-xl text-xs transition ${active ? "font-medium" : "text-muted-foreground"}`} style={{ background: active ? "hsl(var(--vd-green))" : "transparent", color: active ? "white" : undefined }} data-testid={testId}>{children}</button>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5">{Icon && <Icon size={12} />} {label}</div>
      {children}
    </label>
  );
}
